import React, { useState, useEffect } from 'react';
import { GoogleCalendarConnections } from '../components/booking/GoogleCalendarConnections';
import { BookingEmailLog } from '../components/booking/BookingEmailLog';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Link2, Plus, Trash2, RefreshCw, CheckCircle2, XCircle, 
  Clock, AlertTriangle, ChevronDown, ChevronUp, Copy, Eye, EyeOff,
  Building2, FileText
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ExternalIntegrations({ embedded = false }) {
  const [apps, setApps] = useState([]);
  const [pendingItems, setPendingItems] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  
  // New app form
  const [showNewApp, setShowNewApp] = useState(false);
  const [newAppName, setNewAppName] = useState('');
  const [newAppDesc, setNewAppDesc] = useState('');
  const [creatingApp, setCreatingApp] = useState(false);
  
  // Created app credentials (shown once)
  const [newCredentials, setNewCredentials] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  
  // Sections expanded state
  const [expandedSections, setExpandedSections] = useState({
    apps: true,
    pending: true,
    logs: false
  });
  
  // Agency list for mapping
  const [agencies, setAgencies] = useState([]);

  useEffect(() => {
    fetchData();
    fetchAgencies();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [appsRes, pendingRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/oauth/apps`),
        axios.get(`${API}/api/admin/sync/pending`),
        axios.get(`${API}/api/admin/integrations/stats`)
      ]);
      setApps(appsRes.data);
      setPendingItems(pendingRes.data);
      setStats(statsRes.data);
      setSyncLogs(statsRes.data.recent_activity || []);
    } catch (error) {
      console.error('Error fetching integration data:', error);
      toast.error('Failed to load integration data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgencies = async () => {
    try {
      const response = await axios.get(`${API}/api/agency-profiles`);
      setAgencies(response.data);
    } catch (error) {
      console.error('Error fetching agencies:', error);
    }
  };

  const createApp = async () => {
    if (!newAppName.trim()) {
      toast.error('App name is required');
      return;
    }
    
    setCreatingApp(true);
    try {
      const response = await axios.post(`${API}/api/oauth/apps`, {
        name: newAppName.trim(),
        description: newAppDesc.trim() || null
      });
      
      // Show credentials
      setNewCredentials(response.data.app);
      toast.success('App registered! Save the credentials below.');
      
      // Reset form
      setNewAppName('');
      setNewAppDesc('');
      setShowNewApp(false);
      
      // Refresh list
      fetchData();
    } catch (error) {
      console.error('Error creating app:', error);
      toast.error('Failed to create app');
    } finally {
      setCreatingApp(false);
    }
  };

  const revokeApp = async (appId, appName) => {
    if (!window.confirm(`Are you sure you want to revoke access for "${appName}"? This will invalidate all tokens.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/api/oauth/apps/${appId}`);
      toast.success('App access revoked');
      fetchData();
    } catch (error) {
      console.error('Error revoking app:', error);
      toast.error('Failed to revoke app');
    }
  };

  const triggerSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/api/admin/sync/trigger`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      console.error('Error triggering sync:', error);
      toast.error('Failed to trigger sync');
    } finally {
      setSyncing(false);
    }
  };

  const approvePending = async (pendingId, agencyId = null) => {
    try {
      await axios.post(`${API}/api/admin/sync/pending/${pendingId}/approve`, null, {
        params: agencyId ? { agency_id: agencyId } : {}
      });
      toast.success('Item approved');
      fetchData();
    } catch (error) {
      console.error('Error approving item:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const rejectPending = async (pendingId) => {
    try {
      await axios.post(`${API}/api/admin/sync/pending/${pendingId}/reject`);
      toast.success('Item rejected');
      fetchData();
    } catch (error) {
      console.error('Error rejecting item:', error);
      toast.error('Failed to reject');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className={embedded ? "space-y-4" : "p-6 space-y-6 max-w-6xl mx-auto"}>
      <GoogleCalendarConnections />
      <BookingEmailLog />
      {/* Header */}
      {!embedded && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Link2 size={28} />
              External Integrations
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage API connections with external systems
            </p>
          </div>
          <Button onClick={triggerSync} disabled={syncing} data-testid="sync-now-btn">
            <RefreshCw size={16} className={`mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync Now'}
          </Button>
        </div>
      )}

      {embedded && (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground">Manage API connections with external systems</p>
          <Button size="sm" onClick={triggerSync} disabled={syncing} data-testid="sync-now-btn">
            <RefreshCw size={14} className={`mr-1 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync Now'}
          </Button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Link2 className="text-green-600" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats?.connected_apps || 0}</p>
              <p className="text-sm text-muted-foreground">Connected Apps</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <AlertTriangle className="text-orange-600" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats?.pending_review || 0}</p>
              <p className="text-sm text-muted-foreground">Pending Review</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Clock className="text-blue-600" size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold">{syncLogs.length}</p>
              <p className="text-sm text-muted-foreground">Recent Syncs</p>
            </div>
          </div>
        </Card>
      </div>

      {/* New Credentials Display (shown once after creation) */}
      {newCredentials && (
        <Card className="p-6 border-green-300 bg-green-50">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle2 size={20} />
              <h3 className="font-semibold">App Registered Successfully!</h3>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setNewCredentials(null)}
            >
              <XCircle size={16} />
            </Button>
          </div>
          <p className="text-sm text-green-700 mb-4">
            <strong>Save these credentials now!</strong> The client secret won't be shown again.
          </p>
          <div className="space-y-3 bg-white p-4 rounded-lg border">
            <div>
              <label className="text-xs text-muted-foreground">App Name</label>
              <p className="font-medium">{newCredentials.name}</p>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Client ID</label>
              <div className="flex items-center gap-2">
                <code className="bg-muted px-2 py-1 rounded text-sm flex-1 overflow-x-auto">
                  {newCredentials.client_id}
                </code>
                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(newCredentials.client_id)}>
                  <Copy size={14} />
                </Button>
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Client Secret</label>
              <div className="flex items-center gap-2">
                <code className="bg-muted px-2 py-1 rounded text-sm flex-1 overflow-x-auto">
                  {showSecret ? newCredentials.client_secret : '••••••••••••••••••••'}
                </code>
                <Button variant="ghost" size="sm" onClick={() => setShowSecret(!showSecret)}>
                  {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(newCredentials.client_secret)}>
                  <Copy size={14} />
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Connected Apps Section */}
      <Card className="overflow-hidden">
        <div
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50"
          onClick={() => toggleSection('apps')}
        >
          <h2 className="font-semibold flex items-center gap-2">
            <Link2 size={18} />
            Connected Apps ({apps.length})
          </h2>
          {expandedSections.apps ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
        
        {expandedSections.apps && (
          <div className="border-t">
            {apps.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                No apps connected yet.
              </div>
            ) : (
              <div className="divide-y">
                {apps.map((app) => (
                  <div key={app.id} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium">{app.name}</p>
                      {app.description && (
                        <p className="text-sm text-muted-foreground">{app.description}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        Client ID: <code className="bg-muted px-1 rounded">{app.client_id}</code>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Created: {formatDate(app.created_at)}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => revokeApp(app.id, app.name)}
                    >
                      <Trash2 size={14} className="mr-1" />
                      Revoke
                    </Button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Add new app */}
            <div className="p-4 border-t bg-muted/30">
              {showNewApp ? (
                <div className="space-y-3">
                  <Input
                    placeholder="App name (e.g., Accounting System)"
                    value={newAppName}
                    onChange={(e) => setNewAppName(e.target.value)}
                    data-testid="new-app-name"
                  />
                  <Input
                    placeholder="Description (optional)"
                    value={newAppDesc}
                    onChange={(e) => setNewAppDesc(e.target.value)}
                    data-testid="new-app-desc"
                  />
                  <div className="flex gap-2">
                    <Button onClick={createApp} disabled={creatingApp} data-testid="create-app-btn">
                      {creatingApp ? 'Creating...' : 'Create App'}
                    </Button>
                    <Button variant="outline" onClick={() => setShowNewApp(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button variant="outline" onClick={() => setShowNewApp(true)} data-testid="register-app-btn">
                  <Plus size={16} className="mr-2" />
                  Register New App
                </Button>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Pending Review Section */}
      <Card className="overflow-hidden">
        <div
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50"
          onClick={() => toggleSection('pending')}
        >
          <h2 className="font-semibold flex items-center gap-2">
            <AlertTriangle size={18} className={pendingItems.length > 0 ? 'text-orange-500' : ''} />
            Pending Review ({pendingItems.length})
          </h2>
          {expandedSections.pending ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
        
        {expandedSections.pending && (
          <div className="border-t">
            {pendingItems.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <CheckCircle2 size={24} className="mx-auto mb-2 text-green-500" />
                No items pending review.
              </div>
            ) : (
              <div className="divide-y">
                {pendingItems.map((item) => (
                  <div key={item.id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText size={16} className="text-muted-foreground" />
                          <span className="font-medium">
                            Invoice #{item.external_data?.invoice_number}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            item.issue === 'unknown_agency' 
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {item.issue === 'unknown_agency' ? 'Unknown Agency' : item.issue}
                          </span>
                        </div>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p>Agency: <strong>{item.external_data?.agency_name}</strong></p>
                          <p>Amount: <strong>${item.external_data?.amount?.toFixed(2)}</strong></p>
                          <p>Status: {item.external_data?.status}</p>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            onClick={() => approvePending(item.id)}
                            data-testid={`approve-${item.id}`}
                          >
                            <CheckCircle2 size={14} className="mr-1" />
                            Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-red-600"
                            onClick={() => rejectPending(item.id)}
                            data-testid={`reject-${item.id}`}
                          >
                            <XCircle size={14} className="mr-1" />
                            Reject
                          </Button>
                        </div>
                        {item.issue === 'unknown_agency' && (
                          <select
                            className="text-sm border rounded px-2 py-1"
                            onChange={(e) => {
                              if (e.target.value) {
                                approvePending(item.id, e.target.value);
                              }
                            }}
                            defaultValue=""
                          >
                            <option value="">Map to agency...</option>
                            {agencies.map((ag) => (
                              <option key={ag.id} value={ag.id}>
                                {ag.agency_name}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Sync History Section */}
      <Card className="overflow-hidden">
        <div
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50"
          onClick={() => toggleSection('logs')}
        >
          <h2 className="font-semibold flex items-center gap-2">
            <Clock size={18} />
            Sync History
          </h2>
          {expandedSections.logs ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
        
        {expandedSections.logs && (
          <div className="border-t">
            {syncLogs.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                No sync history yet.
              </div>
            ) : (
              <div className="divide-y max-h-[400px] overflow-y-auto">
                {syncLogs.map((log) => (
                  <div key={log.id} className="p-3 flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${
                      log.status === 'success' ? 'bg-green-500' :
                      log.status === 'partial' ? 'bg-orange-500' : 'bg-red-500'
                    }`} />
                    <div className="flex-1">
                      <p className="text-sm">
                        <span className="font-medium">{log.app_name}</span>
                        <span className="text-muted-foreground"> - {log.operation}</span>
                      </p>
                      {log.details && (
                        <p className="text-xs text-muted-foreground">{log.details}</p>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatDate(log.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* API Documentation Link */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <FileText className="text-blue-600 mt-0.5" size={20} />
          <div>
            <p className="font-medium text-blue-900">API Endpoints</p>
            <p className="text-sm text-blue-800 mt-1">
              External apps can use these endpoints after OAuth authentication:
            </p>
            <ul className="text-sm text-blue-700 mt-2 space-y-1 font-mono">
              <li>GET /api/external/agencies - Fetch agency data</li>
              <li>GET /api/external/invoices - Fetch invoices</li>
              <li>POST /api/external/invoices - Push invoice updates</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
}
